// src/components/support/WhySupportSection.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { renderIcon } from '@dopomogai/utils';
import clsx from 'clsx';

interface PillarData {
  icon: string;
  titleKey: string;
  textKey: string;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      ease: 'easeOut',
    },
  },
};

const pillarVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
    },
  },
};

export const WhySupportSection: React.FC = () => {
  const { t } = useTranslation('support_us');

  const pillars: PillarData[] = [
    { icon: 'package', titleKey: 'whySupport.pillars.platform.title', textKey: 'whySupport.pillars.platform.text' },
    { icon: 'heart', titleKey: 'whySupport.pillars.impact.title', textKey: 'whySupport.pillars.impact.text' },
    { icon: 'shield-check', titleKey: 'whySupport.pillars.ethical.title', textKey: 'whySupport.pillars.ethical.text' },
    { icon: 'users', titleKey: 'whySupport.pillars.community.title', textKey: 'whySupport.pillars.community.text' },
  ];

  return (
    <section className="bg-white py-16 md:py-24">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">{t('whySupport.headline')}</h2>
          <p className="mt-4 text-lg text-slate-600">{t('whySupport.intro')}</p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-16"
        >
          {pillars.map((pillar) => (
            // --- THE ANIMATION FIX ---
            // Outer div handles the initial animation and acts as the "group" for the hover effect.
            <motion.div
              key={pillar.titleKey}
              variants={pillarVariants}
              className="relative group h-full"
            >
              {/* The shadow layer */}
              <div className="absolute inset-0 bg-black rounded-xl transition-transform duration-200 ease-out group-hover:translate-x-1 group-hover:translate-y-1"></div>
              
              {/* The content layer that moves on hover */}
              <div className="relative text-center p-6 bg-slate-50 rounded-xl border-2 border-black h-full flex flex-col items-center transition-transform duration-200 ease-out group-hover:-translate-x-1 group-hover:-translate-y-1">
                <div className="inline-flex items-center justify-center p-4 bg-emerald-100 rounded-lg border-2 border-black mb-5">
                  {renderIcon(pillar.icon, { size: 28, className: "text-emerald-700" })}
                </div>
                <h3 className="text-lg font-bold text-gray-900">{t(pillar.titleKey)}</h3>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed flex-grow">{t(pillar.textKey)}</p>
              </div>
            </motion.div>
            // --- END OF FIX ---
          ))}
        </motion.div>
      </div>
    </section>
  );
};

